'use client';

import { useState, useMemo, useEffect, useRef } from 'react';
import { useDebouncedValue } from '@tanstack/react-pacer';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmationDialog } from '@/components/shared/delete-dialog';
import {
    usePresets,
    useCreatePreset,
    useUpdatePreset,
    useDeletePreset,
    usePresetParentCategories,
    presetParentCategoryPillLabel,
    presetParentCategoryToFormValue,
    presetParentCategoryIdToFormValue,
    type PresetParentCategoryItem,
} from '@/hooks/use-presets';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SEARCH_DEBOUNCE_WAIT } from '@/utils/global';
import {
    Plus,
    ChevronLeft,
    Loader2,
    Search,
    MoreHorizontal,
    Edit,
    Trash2,
    X,
} from 'lucide-react';
import { CardSparkleIcon } from './card-sparkle-icon';
import { promptCardCategoryBadgeClass } from './preset-prompt-cards';
import { HorizontalChevronChipRow } from '@/components/prompt-library/parent-category-pill-scroller';
import {
    displayPresetAgentName,
} from '@/components/prompt-library/preset-agent-options';

/** Match command chat empty-state category chips (see `chat-area.tsx`). */
const libraryCategoryChipBase =
    'group shrink-0 inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors sm:h-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const libraryCategoryChipActive =
    'bg-foreground text-background shadow-sm dark:bg-zinc-700 dark:text-zinc-50 dark:shadow-[0_1px_2px_rgba(0,0,0,0.45)]';
const libraryCategoryChipInactive = 'text-muted-foreground hover:text-foreground';

/** Match chat preset cards: violet band via gradient (no border-t / box-shadow seam). */
const sheetPresetCardSurfaceClass =
    'bg-[linear-gradient(180deg,#7c3aed_0px,#7c3aed_4px,hsl(var(--card))_4px,hsl(var(--card))_100%)]';


// --- Form schema ---

const presetFormSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    description: z.string().min(1, 'Description is required'),
    persona: z.string().min(1, 'Persona is required'),
    parent_category_id: z.string().min(1, 'Category is required'),
    parent_category: z.string().min(1, 'Category is required'),
    visibility: z.enum(['PUBLIC', 'PRIVATE']),
    tags: z.array(z.string()),
});

type PresetFormValues = z.infer<typeof presetFormSchema>;

// --- Preset Card (sheet-optimized, single column) ---

function SheetPresetCard({
    preset,
    onEdit,
    onDelete,
    canUpdate,
    canDelete,
}: {
    preset: Preset;
    onEdit: (preset: Preset) => void;
    onDelete: (preset: Preset) => void;
    canUpdate: boolean;
    canDelete: boolean;
}) {
    const sheetCardBadgeLabel =
        presetParentCategoryPillLabel(preset.parent_category_name ?? preset.parent_category ?? '').toUpperCase() || 'PRESET';

    return (
        <Card
            className={cn(
                'relative flex flex-col overflow-hidden rounded-xl border border-border transition-shadow',
                sheetPresetCardSurfaceClass,
                'shadow-sm hover:shadow-md'
            )}
        >
            <div className="flex flex-col gap-2.5 p-4 pt-3">
                <div className="flex min-w-0 items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <Badge variant="secondary" className={promptCardCategoryBadgeClass}>
                            {sheetCardBadgeLabel}
                        </Badge>
                        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{preset.title}</span>
                    </div>
                    {(canUpdate || canDelete) && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-7 w-7 p-0 shrink-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {canUpdate && (
                                    <DropdownMenuItem onClick={() => onEdit(preset)}>
                                        <Edit className="mr-2 h-4 w-4" />
                                        Edit
                                    </DropdownMenuItem>
                                )}
                                {canDelete && (
                                    <DropdownMenuItem className="text-destructive" onClick={() => onDelete(preset)}>
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Delete
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                <h3 className="text-sm font-bold leading-snug text-foreground line-clamp-2">
                    {preset.description}
                </h3>

                {preset.agent_name && (
                    <div className="rounded-lg bg-violet-50 px-3 py-2 dark:bg-violet-950/40">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                            <CardSparkleIcon className="size-3.5 opacity-85" />
                            Recommended agent
                        </div>
                        <p className="mt-0.5 text-xs leading-relaxed text-foreground">
                            {displayPresetAgentName(preset.agent_name)}
                            {preset.agent_description && <> — {preset.agent_description}</>}
                        </p>
                    </div>
                )}

                {preset.tags && preset.tags.length > 0 && (
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        {preset.tags.join(' + ')}
                    </p>
                )}
            </div>
        </Card>
    );
}

// --- Skeleton Card ---

function PresetSkeleton() {
    return (
        <div className="overflow-hidden rounded-xl border border-border bg-[linear-gradient(180deg,hsl(var(--muted))_0px,hsl(var(--muted))_4px,hsl(var(--card))_4px,hsl(var(--card))_100%)] shadow-sm">
            <div className="space-y-2.5 p-4 pt-3">
            <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-10 rounded-full" />
                <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <div className="rounded-lg bg-muted/40 p-2.5 space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-full" />
            </div>
            <Skeleton className="h-3 w-28" />
            </div>
        </div>
    );
}

// --- Preset Form ---

function PresetForm({
    parentCategoryItems,
    parentCategoriesLoading,
    defaultValues,
    onSubmit,
    isPending,
    submitLabel,
    onCancel,
}: {
    parentCategoryItems: PresetParentCategoryItem[];
    parentCategoriesLoading?: boolean;
    defaultValues?: Partial<PresetFormValues>;
    onSubmit: (data: PresetFormValues) => void;
    isPending: boolean;
    submitLabel: string;
    onCancel: () => void;
}) {
    const [tagInput, setTagInput] = useState('');

    const form = useForm<PresetFormValues>({
        resolver: zodResolver(presetFormSchema),
        defaultValues: {
            title: '',
            description: '',
            persona: '',
            parent_category_id: parentCategoryItems[0]?.id ?? '',
            parent_category: parentCategoryItems[0]?.name ?? '',
            visibility: 'PRIVATE',
            tags: [],
            ...(defaultValues ?? {}),
        },
    });

    const watchedParentCategoryId = form.watch('parent_category_id');
    useEffect(() => {
        if (!parentCategoryItems.length) return;
        const idCur = form.getValues('parent_category_id');
        const nameCur = form.getValues('parent_category');
        const byId = parentCategoryItems.some((i) => i.id === idCur);
        const byName = parentCategoryItems.some((i) => i.name === nameCur);
        if (!byId || !byName) {
            const first = parentCategoryItems[0];
            form.setValue('parent_category_id', first.id);
            form.setValue('parent_category', first.name);
        }
    }, [parentCategoryItems, form]);

    const tags = form.watch('tags');

    const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const value = tagInput.trim();
            if (value && !tags.includes(value)) {
                form.setValue('tags', [...tags, value]);
                setTagInput('');
            }
        }
    };

    const handleRemoveTag = (tag: string) => {
        form.setValue('tags', tags.filter((t) => t !== tag));
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 px-6 py-5 flex-1 overflow-y-auto">
                <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormDescription>Short name shown in your prompt library lists.</FormDescription>
                            <FormControl>
                                <Input placeholder="e.g. My morning sales check" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormDescription>Summary shown on preset cards and in search results.</FormDescription>
                            <FormControl>
                                <Textarea placeholder="Brief description..." rows={2} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="persona"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Command</FormLabel>
                            <FormDescription>
                                Full instructions sent to the model when this preset is applied (your system /
                                persona prompt).
                            </FormDescription>
                            <FormControl>
                                <Textarea
                                    placeholder="You are a sales analyst that reviews overnight CRM activity..."
                                    rows={6}
                                    className="font-mono text-sm"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                        control={form.control}
                        name="visibility"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Visibility</FormLabel>
                                <FormDescription>Private:you can see it. Public:visible to others.</FormDescription>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        <SelectItem value="PRIVATE">Personal</SelectItem>
                                        <SelectItem value="PUBLIC">Team</SelectItem>
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                        control={form.control}
                        name="parent_category_id"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Category</FormLabel>
                                <FormDescription>Top-level workflow group (e.g. Executive or Management).</FormDescription>
                                <Select
                                    onValueChange={(id) => {
                                        field.onChange(id);
                                        const item = parentCategoryItems.find((i) => i.id === id);
                                        form.setValue('parent_category', item?.name ?? '');
                                    }}
                                    value={field.value}
                                    disabled={
                                        parentCategoriesLoading ||
                                        parentCategoryItems.length === 0
                                    }
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue
                                                placeholder={
                                                    parentCategoriesLoading
                                                        ? 'Loading categories…'
                                                        : parentCategoryItems.length === 0
                                                          ? 'No categories available'
                                                          : 'Select category'
                                                }
                                            />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {parentCategoryItems.map((item) => (
                                            <SelectItem key={item.id} value={item.id}>
                                                {presetParentCategoryPillLabel(item.name)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                </div>

                {/* Tags */}
                <div className="space-y-2">
                    <div className="space-y-1.5">
                        <FormLabel>Tags</FormLabel>
                        <FormDescription>
                            Press Enter to add a tag. Tags appear above the input.
                        </FormDescription>
                    </div>
                    {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {tags.map((tag) => (
                                <Badge key={tag} variant="secondary" className="gap-1 pr-1 text-xs">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTag(tag)}
                                        className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}
                    <Input
                        placeholder="Type a tag and press Enter"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleAddTag}
                    />
                </div>

                {/* Actions pinned at bottom */}
                <div className="flex gap-3 pt-2 mt-auto">
                    <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button type="submit" className="flex-1" disabled={isPending}>
                        {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        {submitLabel}
                    </Button>
                </div>
            </form>
        </Form>
    );
}

// --- Main Sheet ---

interface PromptLibrarySheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function PromptLibrarySheet({ open, onOpenChange }: PromptLibrarySheetProps) {
    const queryClient = useQueryClient();
    const wasOpenRef = useRef(false);

    const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
    const [editingPreset, setEditingPreset] = useState<Preset | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Preset | null>(null);

    // Filters — parent is always one of the API rows (no "All").
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, { wait: SEARCH_DEBOUNCE_WAIT });
    const [parentCategoryFilter, setParentCategoryFilter] = useState('');
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            queryClient.invalidateQueries({ queryKey: ['preset-parent-categories'] });
            queryClient.invalidateQueries({ queryKey: ['user-presets'] });
        }
        wasOpenRef.current = open;
    }, [open, queryClient]);

    const { data: presetParentsResponse, isLoading: isLoadingPresetParents } = usePresetParentCategories({
        enabled: open,
    });
    const parentCategoryItems = presetParentsResponse?.items ?? [];

    useEffect(() => {
        const items = presetParentsResponse?.items;
        if (!items?.length) return;
        setParentCategoryFilter((prev) => {
            if (prev && items.some((i) => i.id === prev)) return prev;
            return items[0]!.id;
        });
    }, [presetParentsResponse?.items]);

    const {
        presets,
        promptItems,
        canListPresets,
        isLoading,
        isFetchingNextPage: isFetchingNextPresets,
        loadMoreRef: presetsLoadMoreRef,
    } = usePresets({
        parentCategoryId: parentCategoryFilter || undefined,
        search: debouncedSearch || undefined,
        myPresets: true,
        enabled: open && Boolean(parentCategoryFilter),
    });

    const createMutation = useCreatePreset();
    const updateMutation = useUpdatePreset();
    const deleteMutation = useDeletePreset();

    const handleCreate = (data: PresetFormValues) => {
        createMutation.mutate(data, {
            onSuccess: () => {
                toast.success('Preset created');
                setView('list');
            },
            onError: (error: any) => {
                toast.error('Failed to create preset', {
                    description: error.response?.data?.message || 'An unexpected error occurred',
                });
            },
        });
    };

    const handleUpdate = (data: PresetFormValues) => {
        if (!editingPreset) return;
        updateMutation.mutate(
            { id: editingPreset.id, data },
            {
                onSuccess: () => {
                    toast.success('Preset updated');
                    setView('list');
                    setEditingPreset(null);
                },
                onError: (error: any) => {
                    toast.error('Failed to update preset', {
                        description: error.response?.data?.message || 'An unexpected error occurred',
                    });
                },
            }
        );
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        deleteMutation.mutate(deleteTarget.id, {
            onSuccess: () => {
                toast.success('Preset deleted');
                setDeleteTarget(null);
            },
            onError: (error: any) => {
                toast.error('Failed to delete preset', {
                    description: error.response?.data?.message || 'An unexpected error occurred',
                });
            },
        });
    };

    // Reset view and list filters when sheet closes; parent re-syncs from API when reopened
    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            setView('list');
            setEditingPreset(null);
            setDeleteTarget(null);
            setParentCategoryFilter('');
            setSearch('');
        }
        onOpenChange(nextOpen);
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleOpenChange}>
                <SheetContent
                    side="right"
                    className="w-full max-w-[100vw] sm:w-[50vw] sm:max-w-[50vw] min-w-0 overflow-hidden p-0 flex flex-col [&>button]:z-30 [&>button]:top-4 [&>button]:right-4"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    {/* ========== LIST VIEW ========== */}
                    {view === 'list' && (
                        <>
                            <SheetHeader className="px-6 pt-6 pb-4 border-b bg-background sticky top-0 z-20 space-y-3">
                                <div className="flex items-center justify-between pr-8">
                                    <div>
                                        <SheetTitle className="text-xl font-semibold">Prompt Library</SheetTitle>
                                        <SheetDescription className="text-sm text-muted-foreground mt-1">
                                            Your presets and prompts
                                        </SheetDescription>
                                    </div>
                                    {createMutation.hasPermission && (
                                        <Button size="sm" onClick={() => setView('create')}>
                                            <Plus className="mr-1.5 h-4 w-4" />
                                            Create
                                        </Button>
                                    )}
                                </div>

                                {/* Filters — parent pills (chat-style); sub-categories as pills below */}
                                <div className="flex flex-col gap-3">
                                    <div className="relative">
                                        <Input
                                            className="pl-8 h-9"
                                            placeholder="Search presets..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            startIcon={<Search className="h-4 w-4 text-muted-foreground" />}
                                        />
                                    </div>
                                    <HorizontalChevronChipRow
                                        itemCount={
                                            parentCategoryItems.length +
                                            (isLoadingPresetParents &&
                                            parentCategoryItems.length === 0
                                                ? 1
                                                : 0)
                                        }
                                    >
                                        <div className="flex w-max min-w-0 flex-nowrap items-center gap-2 sm:gap-3">
                                            {isLoadingPresetParents &&
                                            parentCategoryItems.length === 0 ? (
                                                <Loader2
                                                    className="size-4 shrink-0 animate-spin text-muted-foreground"
                                                    aria-label="Loading categories"
                                                />
                                            ) : null}
                                            {parentCategoryItems.map((item) => (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() =>
                                                        setParentCategoryFilter(item.id)
                                                    }
                                                    className={cn(
                                                        libraryCategoryChipBase,
                                                        'whitespace-nowrap',
                                                        parentCategoryFilter === item.id
                                                            ? libraryCategoryChipActive
                                                            : libraryCategoryChipInactive
                                                    )}
                                                >
                                                    {presetParentCategoryPillLabel(
                                                        item.name
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </HorizontalChevronChipRow>
                                </div>
                            </SheetHeader>

                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                {!canListPresets ? (
                                    <div className="text-center py-16">
                                        <p className="text-sm text-muted-foreground">
                                            You do not have permission to view command presets
                                        </p>
                                    </div>
                                ) : isLoading ? (
                                    <div className="space-y-3">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <PresetSkeleton key={i} />
                                        ))}
                                    </div>
                                ) : presets.length === 0 ? (
                                    <div className="text-center py-16">
                                        <p className="text-sm text-muted-foreground">No presets found</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {presets.map((preset) => (
                                            <SheetPresetCard
                                                key={preset.id}
                                                preset={preset}
                                                canUpdate={updateMutation.hasPermission}
                                                canDelete={deleteMutation.hasPermission}
                                                onEdit={(p) => {
                                                    setEditingPreset(p);
                                                    setView('edit');
                                                }}
                                                onDelete={(p) => setDeleteTarget(p)}
                                            />
                                        ))}
                                        {/* Infinite scroll sentinel */}
                                        <div ref={presetsLoadMoreRef} className="py-2">
                                            {isFetchingNextPresets && (
                                                <div className="space-y-3">
                                                    <PresetSkeleton />
                                                    <PresetSkeleton />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* ========== CREATE VIEW ========== */}
                    {view === 'create' && (
                        <>
                            <SheetHeader className="px-6 pt-6 pb-4 border-b bg-background sticky top-0 z-20">
                                <div className="flex items-center gap-3 pr-8">
                                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setView('list')}>
                                        <ChevronLeft className="h-5 w-5" />
                                    </Button>
                                    <div>
                                        <SheetTitle className="text-lg font-semibold">Create Preset</SheetTitle>
                                        <SheetDescription className="text-sm text-muted-foreground">
                                            Add a new prompt preset
                                        </SheetDescription>
                                    </div>
                                </div>
                            </SheetHeader>
                            <PresetForm
                                parentCategoryItems={parentCategoryItems}
                                parentCategoriesLoading={isLoadingPresetParents}
                                onSubmit={handleCreate}
                                isPending={createMutation.isPending}
                                submitLabel="Create Preset"
                                onCancel={() => setView('list')}
                            />
                        </>
                    )}

                    {/* ========== EDIT VIEW ========== */}
                    {view === 'edit' && editingPreset && (
                        (() => {
                            const editingPresetParentCategory =
                                editingPreset.parent_category ??
                                (editingPreset as any).parent_category_name ??
                                '';
                            return (
                        <>
                            <SheetHeader className="px-6 pt-6 pb-4 border-b bg-background sticky top-0 z-20">
                                <div className="flex items-center gap-3 pr-8">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-11 w-11 shrink-0"
                                        onClick={() => {
                                            setView('list');
                                            setEditingPreset(null);
                                        }}
                                    >
                                        <ChevronLeft className="h-7 w-7" />
                                    </Button>
                                    <div>
                                        <SheetTitle className="text-lg font-semibold">Edit Preset</SheetTitle>
                                        <SheetDescription className="text-sm text-muted-foreground">
                                            Update your preset
                                        </SheetDescription>
                                    </div>
                                </div>
                            </SheetHeader>
                            <PresetForm
                                key={editingPreset.id}
                                parentCategoryItems={parentCategoryItems}
                                parentCategoriesLoading={isLoadingPresetParents}
                                defaultValues={{
                                    title: editingPreset.title,
                                    description: editingPreset.description,
                                    persona: editingPreset.persona,
                                    parent_category_id: presetParentCategoryIdToFormValue(
                                        editingPresetParentCategory,
                                        editingPreset.parent_category_id,
                                        parentCategoryItems
                                    ),
                                    parent_category: presetParentCategoryToFormValue(
                                        editingPresetParentCategory,
                                        parentCategoryItems
                                    ),
                                    visibility: editingPreset.visibility,
                                    tags: editingPreset.tags || [],
                                }}
                                onSubmit={handleUpdate}
                                isPending={updateMutation.isPending}
                                submitLabel="Save Changes"
                                onCancel={() => {
                                    setView('list');
                                    setEditingPreset(null);
                                }}
                            />
                        </>
                            );
                        })()
                    )}
                </SheetContent>
            </Sheet>

            <DeleteConfirmationDialog
                open={!!deleteTarget}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                onConfirm={handleDelete}
                isLoading={deleteMutation.isPending}
                itemType="preset"
            />
        </>
    );
}
